import {
  CurrencyPipe,
  NgClass,
  ViewportScroller
} from '@angular/common';
import {
  Component,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  untracked
} from '@angular/core';
import {
  FormControl,
  ReactiveFormsModule,
  Validators
} from '@angular/forms';
import { NgxTolgeeModule } from '@tolgee/ngx';
import { Subscription } from 'rxjs';

import { sanitizeAquaoText } from '../../../../core/helpers/aquao-text.helper';
import {
  AQUAO_ADDRESS_MAX,
  AQUAO_CITY_MAX,
  AQUAO_DESTINATAIRE_MAX,
  AQUAO_EMAIL_MAX,
  AQUAO_OFFREUR_MAX,
  AQUAO_TEXT_UX_MAX
} from '../../../../core/model/aquao-field-limits.model';
import {
  GiftCartItem,
  GiftFormat
} from '../../../../core/model/cart/cart-item.model';
import { ProductType } from '../../../../core/model/product.model';
import { emulateClick } from '../core/a11y';
import { CharacterCountComponent } from '../shared/components/character-count/character-count.component';
import { DatePickerComponent } from '../shared/date-picker/date-picker.component';
import { HoverGradientDirective } from '../shared/directives/hover-gradient.directive';
import { ProgressClickDirective } from '../shared/directives/progress-click.directive';
import { Product } from '../shared/model/product.model';
import { AuthService } from '../shared/services/auth.service';
import { ProductService } from '../shared/services/product.service';
import { SiteService } from '../shared/services/site.service';
import { FrenchZipValidator } from '../shared/validators/validators';
import { createGiftFormGroup } from './gift-form.factory';
import {
  GiftForm,
  GiftFormGroup
} from './gift-form.model';

/** Champs cadeau transmis à Aquao dont la saisie doit être nettoyée du charset non supporté. */
const AQUAO_TEXT_FIELDS: (keyof GiftForm)[] = [
  'from',
  'firstName',
  'lastName',
  'email',
  'address',
  'city',
  'message'
];

/** Longueur UX du code postal français. */
const FRENCH_ZIP_LENGTH = 5;

@Component({
  selector: 'app-gift-form',
  imports: [
    HoverGradientDirective,
    NgxTolgeeModule,
    ReactiveFormsModule,
    NgClass,
    ProgressClickDirective,
    DatePickerComponent,
    CurrencyPipe,
    CharacterCountComponent
  ],
  templateUrl: './gift-form.component.html'
})
export class GiftFormComponent {
  readonly emulateClick = emulateClick;
  readonly GiftFormat = GiftFormat;
  readonly AQUAO_OFFREUR_MAX = AQUAO_OFFREUR_MAX;
  readonly AQUAO_DESTINATAIRE_MAX = AQUAO_DESTINATAIRE_MAX;
  readonly AQUAO_EMAIL_MAX = AQUAO_EMAIL_MAX;
  readonly AQUAO_ADDRESS_MAX = AQUAO_ADDRESS_MAX;
  readonly AQUAO_CITY_MAX = AQUAO_CITY_MAX;
  readonly AQUAO_TEXT_UX_MAX = AQUAO_TEXT_UX_MAX;
  readonly FRENCH_ZIP_LENGTH = FRENCH_ZIP_LENGTH;
  authService = inject(AuthService);
  siteService = inject(SiteService);
  productService = inject(ProductService);
  viewportScroller = inject(ViewportScroller);

  id = Math.random().toString(36).substring(2);

  editOnLoad = input(false);
  gift = input.required<GiftCartItem>();
  isCoupon = computed(() => {
    const product = this.productService.get(this.gift().product);
    return product instanceof Product && product.type === ProductType.GiftCertificate;

  });
  giftFormSubmitted = output<GiftCartItem>();
  canBeSubmitted = input<boolean>(true);
  theme = input<'gift' | 'checkout'>('gift');
  giftForm = computed(() => createGiftFormGroup(this.gift()));
  shipDateAsDate = computed(() => {
    const form = this.giftForm();
    const date = form.get('shipDate')?.value ?? new Date().toISOString().slice(0, 10);
    const [ year, month, day ] = date.split('-').map(Number);
    return new Date(Date.UTC(year, month - 1, day))
  })
  today = new Date();
  displayAddressForm = signal<boolean>(false);
  sentToMe = signal<boolean>(false);
  edit = signal(false);
  handleEdit = input<boolean>(true);
  cities = signal<string[] | null>(null);
  displayCities = computed(() => {
    const cities = this.cities();
    const current = this.giftForm().get('city')?.value;
    return cities?.every(c => c !== current) ?? false;
  });

  constructor() {
    effect(() => {
      if (this.editOnLoad()) {
        this.edit.set(true);
      }
    });
    effect(onCleanup => {
      const form = this.giftForm();
      const subscriptions = untracked(() => this.initGiftForm(form));
      onCleanup(() => subscriptions.forEach(subscription => subscription.unsubscribe()));
    });
  }

  // La convention projet impose un @returns même sur une méthode void
  // eslint-disable-next-line jsdoc/require-returns-check
  /**
   * Submit gift form
   * @returns Rien.
   */
  submit = async (): Promise<void> => {
    this.giftFormSubmitted.emit(new GiftCartItem({ ...this.giftForm().getRawValue() }));
    this.edit.set(false);
    this.viewportScroller.scrollToPosition([ 0, 0 ]);
  }

  // La convention projet impose un @returns même sur une méthode void
  // eslint-disable-next-line jsdoc/require-returns-check
  /**
   * Met à jour la date d'expédition du cadeau.
   * @param shipDate Date sélectionnée, ou `undefined` si aucune date n'est choisie.
   * @returns Rien.
   */
  updateShipDate(shipDate: Date | undefined): void {
    if (!shipDate) {
      return;
    }
    const dateStr = new Date(Date.UTC(shipDate.getUTCFullYear(), shipDate.getUTCMonth(), shipDate.getUTCDate()))
      .toISOString()
      .slice(0, 10);
    this.giftForm().get('shipDate')?.setValue(dateStr);
  }

  // La convention projet impose un @returns même sur une méthode void
  // eslint-disable-next-line jsdoc/require-returns-check
  /**
   * Sélectionne une commune proposée pour le code postal saisi.
   * @param city Nom de commune choisi dans la liste codes-postaux.
   * @returns Rien.
   */
  setCity(city: string): void {
    this.cities.set(null);
    this.giftForm().get('city')?.setValue(city);
    this.giftForm().updateValueAndValidity();
  }

  /**
   * Initialise send to me and format form fields and listeners
   * @param form Formulaire cadeau nouvellement créé.
   * @returns Abonnements à nettoyer lorsque le formulaire change ou que le composant est détruit.
   */
  private initGiftForm(form: GiftFormGroup): Subscription[] {
    // On first load check if form has never filled
    if (!this.gift().from) {
      this.edit.set(true);
    }
    // Initialise send to me
    const sentToMeControl = form.get('sentToMe')!;
    this.sendToMeChange(sentToMeControl.value, form, false);
    // Initialise format
    const formatControl = form.get('format')!;
    this.formatChange(formatControl.value, form, false);
    // Add sent to me form listener
    const subscriptions = [ sentToMeControl.valueChanges.subscribe(sentToMe => {
      this.sendToMeChange(sentToMe, form, true);
    }) ];
    // Add format form listener
    subscriptions.push(formatControl.valueChanges.subscribe(format => {
      this.formatChange(format, form, true);
    }));
    // Add charset listeners on every field sent to Aquao
    subscriptions.push(...this.watchAquaoTextFields(form));
    subscriptions.push(this.watchZipForCities(form));
    return subscriptions;
  }

  /**
   * Filtre le CP (chiffres seuls) et propose les communes via codes-postaux.
   * @param form Formulaire cadeau
   * @returns Abonnement à nettoyer avec le formulaire
   */
  private watchZipForCities(form: GiftFormGroup): Subscription {
    const zipControl = form.controls.zip;
    return zipControl.valueChanges.subscribe(async zip => {
      this.setCity('');
      const digitsOnly = this.normalizeFrenchZip(zip);
      if (digitsOnly !== zip) {
        zipControl.setValue(digitsOnly, { emitEvent: false });
      }
      await this.refreshCities(digitsOnly);
    });
  }

  /**
   * Conserve uniquement les chiffres d'un CP français, tronqués à 5.
   * @param zip Valeur saisie ou issue du profil
   * @returns CP normalisé
   */
  private normalizeFrenchZip(zip: string): string {
    return zip.replace(/[^0-9]/g, '').slice(0, FRENCH_ZIP_LENGTH);
  }

  /**
   * Met à jour la liste des communes proposées pour un code postal.
   * @param zip Code postal déjà normalisé
   * @returns Promise résolue une fois la liste mise à jour
   */
  private async refreshCities(zip: string): Promise<void> {
    const { default: codePostaux } = await import('codes-postaux');
    const cities = (codePostaux.find(zip) as { nomCommune: string }[]).map(c => c.nomCommune);
    this.cities.set(cities.length ? cities : null);
  }

  /**
   * Nettoie en continu les champs texte du cadeau des caractères refusés par Aquao
   * @param form The form group
   * @returns Abonnements à nettoyer lorsque le formulaire change ou que le composant est détruit.
   */
  private watchAquaoTextFields(form: GiftFormGroup): Subscription[] {
    return AQUAO_TEXT_FIELDS.map(field => {
      const control = form.get(field) as FormControl<string>;
      return control.valueChanges.subscribe((value: string) => {
        const sanitizedValue = sanitizeAquaoText(value);
        if (sanitizedValue !== value) {
          control.setValue(sanitizedValue, { emitEvent: false });
        }
      });
    });
  }

  /**
   * Construit la valeur destinataire sanitised affichée par le compteur combiné.
   * @param form The form group
   * @returns Prénom et nom nettoyés, concaténés avec un espace et sans espaces périphériques.
   */
  combinedRecipientName(form: GiftFormGroup): string {
    const firstName = sanitizeAquaoText(form.controls.firstName.value).trim();
    const lastName = sanitizeAquaoText(form.controls.lastName.value).trim();
    return `${firstName} ${lastName}`.trim();
  }

  // La convention projet impose un @returns même sur une méthode void
  // eslint-disable-next-line jsdoc/require-returns-check
  /**
   * Actions when format value change
   * @param format The Format value
   * @param form The form group
   * @param resetValues Si vrai, vide ou préremplit les champs selon le format (changement utilisateur).
   * @returns Rien.
   */
  private formatChange(format: GiftFormat, form: GiftFormGroup, resetValues: boolean): void {
    this.displayAddressForm.set(format === GiftFormat.mail);
    const sentToMeControl = form.get('sentToMe')!;
    this.updateFormValues(sentToMeControl.value, format, form, resetValues);
  }

  // La convention projet impose un @returns même sur une méthode void
  // eslint-disable-next-line jsdoc/require-returns-check
  /**
   * Actions when send to me value change
   * @param sentToMe Send to me value
   * @param form The form group
   * @param resetValues Si vrai, ajuste la date d'expédition (changement utilisateur).
   * @returns Rien.
   */
  private sendToMeChange(sentToMe: boolean, form: GiftFormGroup, resetValues: boolean): void {
    this.sentToMe.set(sentToMe);
    if (sentToMe) {
      form.get('shipDate')?.removeValidators(Validators.required);
      if (resetValues) {
        form.get('shipDate')?.setValue('');
      }
    } else {
      form.get('shipDate')?.addValidators(Validators.required);
      if (resetValues && !form.get('shipDate')?.value) {
        form.get('shipDate')?.setValue(new Date().toISOString().slice(0, 10));
      }
    }
    form.controls.shipDate.updateValueAndValidity({ emitEvent: false });
    const formatControl = form.get('format')!;
    this.updateFormValues(sentToMe, formatControl.value, form, resetValues);
  }

  // La convention projet impose un @returns même sur une méthode void
  // eslint-disable-next-line jsdoc/require-returns-check
  /**
   * Disable or enable firstname and last name validators and values
   * @param sendToMe Send to me value
   * @param format The format value
   * @param form The form group
   * @param resetValues Si vrai, écrase les champs liés au format ; si faux, conserve les valeurs persistées.
   * @returns Rien.
   */
  private updateFormValues(
    sendToMe: boolean,
    format: GiftFormat,
    form: GiftFormGroup,
    resetValues: boolean
  ): void {
    const user = this.authService.user();
    if (user && !form.get('from')?.value) {
      form.get('from')?.setValue(`${user.firstName} ${user.lastName}`.trim());
    }
    const cityControl = form.get('city');
    const zipControl = form.get('zip');
    const addressControl = form.get('address');
    const emailControl = form.get('email');
    if (format === GiftFormat.mail) {
      emailControl?.removeValidators(Validators.required);
      cityControl?.addValidators(Validators.required);
      zipControl?.addValidators(Validators.required);
      zipControl?.addValidators(FrenchZipValidator);
      addressControl?.addValidators(Validators.required);
      if (resetValues) {
        emailControl?.setValue('');
        // L'envoi postal ne se fait qu'en France : une adresse de profil étrangère n'est pas reprise
        if (user && sendToMe && (!user.country || user.country === 'FRANCE')) {
          addressControl?.setValue(user.address);
          const normalizedZip = this.normalizeFrenchZip(user.zip);
          zipControl?.setValue(normalizedZip, { emitEvent: false });
          cityControl?.setValue(user.city, { emitEvent: false });
          void this.refreshCities(normalizedZip);
        } else {
          addressControl?.setValue('');
          zipControl?.setValue('', { emitEvent: false });
          cityControl?.setValue('', { emitEvent: false });
          this.cities.set(null);
        }
      }
    } else {
      cityControl?.removeValidators(Validators.required);
      zipControl?.removeValidators(Validators.required);
      zipControl?.removeValidators(FrenchZipValidator);
      addressControl?.removeValidators(Validators.required);
      emailControl?.addValidators(Validators.required);
      if (resetValues) {
        addressControl?.setValue('');
        zipControl?.setValue('', { emitEvent: false });
        cityControl?.setValue('', { emitEvent: false });
        this.cities.set(null);
        if (user && sendToMe) {
          emailControl?.setValue(user.email);
        } else {
          emailControl?.setValue('');
        }
      }
    }
    if (format === GiftFormat.mail && !sendToMe) {
      form.get('firstName')?.addValidators(Validators.required);
      form.get('lastName')?.addValidators(Validators.required);
    } else {
      form.get('firstName')?.removeValidators(Validators.required);
      form.get('lastName')?.removeValidators(Validators.required);
      if (resetValues) {
        form.get('firstName')?.setValue('');
        form.get('lastName')?.setValue('');
      }
    }
    [
      form.controls.city,
      form.controls.zip,
      form.controls.address,
      form.controls.email,
      form.controls.firstName,
      form.controls.lastName
    ].forEach(control => control.updateValueAndValidity({ emitEvent: false }));
    form.updateValueAndValidity({ emitEvent: false });
  }
}
